/**
 * Renders a DiagnosisReport.
 *
 * Everything is rendered as text nodes. No HTML from the model, the OpenAPI
 * specification or the pasted input is ever injected into the DOM, which makes
 * stored-XSS through model output impossible.
 */

import type { ReactNode } from 'react';
import type { DiagnosisReport, FindingKind } from '@core/types';
import { Alert, Badge, CodeBlock, KindBadge, Stat } from './ui';
import { useCopy } from '../lib/hooks';
import { formatDateTime, formatTitleCase, pluralise } from '../lib/format';

function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="row row--between" style={{ marginBottom: 12 }}>
      <h3 style={{ margin: 0 }}>{children}</h3>
      {aside}
    </div>
  );
}

function CopyButton({ text, id, label = 'Copy' }: { text: string; id: string; label?: string }) {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      className="button--small"
      onClick={() => copy(text, id)}
      aria-label={label}
    >
      {copied === id ? 'Copied' : label}
    </button>
  );
}

function kindLabel(kind: FindingKind): string {
  if (kind === 'confirmed') return 'Confirmed by the submitted evidence';
  if (kind === 'likely') return 'Likely, based on a well-known pattern';
  return 'Speculative — verify before acting';
}

export interface ReportViewProps {
  report: DiagnosisReport;
  sanitizedInput: string;
  createdAt: string;
  expiresAt: string | null;
  /** Extra controls rendered in the header (export, share). */
  actions?: ReactNode;
  /** Show the sanitized paste block. Hidden on public share pages by default. */
  showSanitizedInput?: boolean;
}

export function ReportView({
  report,
  sanitizedInput,
  createdAt,
  expiresAt,
  actions,
  showSanitizedInput = false,
}: ReportViewProps) {
  const openApi = report.openApiValidation;
  const errorFindings = report.deterministicFindings.filter((f) => f.severity === 'error');
  const otherFindings = report.deterministicFindings.filter((f) => f.severity !== 'error');

  return (
    <div className="stack">
      <section className="card" aria-labelledby="report-summary">
        <div className="row row--between" style={{ alignItems: 'flex-start' }}>
          <SectionTitle>
            <span id="report-summary">Summary</span>
          </SectionTitle>
          {actions ? <div className="row">{actions}</div> : null}
        </div>

        <div className="stat-row" style={{ marginBottom: 16 }}>
          <Stat value={report.counts.confirmed} label="Confirmed" />
          <Stat value={report.counts.likely} label="Likely" />
          <Stat value={report.counts.speculative} label="Speculative" />
        </div>

        <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text)' }}>{report.executiveSummary}</p>

        <div className="row small faint" style={{ gap: 12 }}>
          <span>
            Input type: <code>{report.detectedInputType}</code> ({report.inputTypeSource})
          </span>
          <span>
            Provider: <code>{report.provider.name}</code>
            {report.provider.model ? ` (${report.provider.model})` : ''}{' '}
            {report.provider.usedAi ? 'AI-assisted' : 'deterministic only'}
          </span>
          <span>Generated {formatDateTime(createdAt)}</span>
          {expiresAt ? <span>Expires {formatDateTime(expiresAt)}</span> : null}
        </div>
      </section>

      {report.mostLikelyRootCause ? (
        <section className="card" aria-labelledby="report-root-cause">
          <SectionTitle>
            <span id="report-root-cause">Most likely root cause</span>
          </SectionTitle>
          <div className={`cause cause--${report.mostLikelyRootCause.kind}`}>
            <div className="cause__head">
              <p className="cause__title">{report.mostLikelyRootCause.title}</p>
              <span className="row" style={{ gap: 6 }}>
                <KindBadge kind={report.mostLikelyRootCause.kind} />
                <Badge>{report.mostLikelyRootCause.confidence}%</Badge>
              </span>
            </div>
            <p className="cause__explanation">{report.mostLikelyRootCause.explanation}</p>
            <p className="small faint">{kindLabel(report.mostLikelyRootCause.kind)}</p>
            {report.mostLikelyRootCause.evidence.length > 0 ? (
              <ul className="evidence-list">
                {report.mostLikelyRootCause.evidence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="card" aria-labelledby="report-causes">
        <SectionTitle>
          <span id="report-causes">Ranked likely causes</span>
          <span className="small faint">
            {report.likelyCauses.length} {pluralise(report.likelyCauses.length, 'cause')}
          </span>
        </SectionTitle>
        {report.likelyCauses.length === 0 ? (
          <p className="faint">
            No cause could be identified from the submitted text. Add the failing HTTP response
            (status line and headers) and run the diagnosis again.
          </p>
        ) : (
          <div className="stack stack--tight">
            {report.likelyCauses.map((cause) => (
              <div key={cause.id} className={`cause cause--${cause.kind}`}>
                <div className="cause__head">
                  <p className="cause__title">
                    {cause.title}{' '}
                    <span className="small faint">
                      ({formatTitleCase(cause.category)} · {cause.source})
                    </span>
                  </p>
                  <span className="row" style={{ gap: 6 }}>
                    <KindBadge kind={cause.kind} />
                    <Badge>{cause.confidence}%</Badge>
                  </span>
                </div>
                {cause.explanation ? (
                  <p className="cause__explanation">{cause.explanation}</p>
                ) : null}
                {cause.evidence.length > 0 ? (
                  <ul className="evidence-list">
                    {cause.evidence.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card" aria-labelledby="report-openapi">
        <SectionTitle>
          <span id="report-openapi">OpenAPI validation</span>
          {openApi.available ? (
            <Badge kind={openApi.depth === 'full' ? 'pro' : 'default'}>{openApi.depth}</Badge>
          ) : null}
        </SectionTitle>

        {!openApi.available ? (
          <p className="faint">
            {openApi.reason ??
              'No OpenAPI specification was supplied, so no contract validation was performed.'}
          </p>
        ) : (
          <div className="stack stack--tight">
            <div className="small faint">
              Specification: <strong>{openApi.specName ?? '(unnamed)'}</strong>
              {openApi.matchedOperation ? (
                <>
                  {' '}
                  · matched operation{' '}
                  <code>
                    {openApi.matchedOperation.method} {openApi.matchedOperation.path}
                  </code>
                  {openApi.matchedOperation.operationId
                    ? ` (${openApi.matchedOperation.operationId})`
                    : ''}
                </>
              ) : (
                <>
                  {' '}
                  · no operation matched <code>{openApi.requestedMethod}</code>{' '}
                  <code>{openApi.requestedPath}</code>
                </>
              )}
            </div>

            {openApi.findings.length === 0 ? (
              <Alert tone="success" title="No specification violations found">
                Every part of the submission that could be checked against the specification passed.
                This is not proof that the API accepted the request.
              </Alert>
            ) : (
              <ul className="plain-list">
                {openApi.findings.map((finding, index) => (
                  <li key={`${finding.code}-${index}`}>
                    <strong>{finding.severity}</strong> · {finding.message}{' '}
                    <span className="faint">
                      ({formatTitleCase(finding.location)} · {finding.code})
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {openApi.checksSkipped.length > 0 ? (
              <details>
                <summary>Checks that were skipped</summary>
                <ul className="plain-list">
                  {openApi.checksSkipped.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        )}
      </section>

      <section className="card" aria-labelledby="report-steps">
        <SectionTitle>
          <span id="report-steps">Step-by-step troubleshooting</span>
        </SectionTitle>
        {report.troubleshootingSteps.length === 0 ? (
          <p className="faint">No steps were produced.</p>
        ) : (
          <ol className="steps">
            {report.troubleshootingSteps.map((step) => (
              <li key={`${step.order}-${step.title}`}>
                <strong>{step.title}</strong>
                {step.detail && step.detail !== step.title ? <span>{step.detail}</span> : null}
                {step.whyItMatters ? <div className="why">{step.whyItMatters}</div> : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="card" aria-labelledby="report-corrected">
        <SectionTitle>
          <span id="report-corrected">Corrected example</span>
          {report.correctedExample ? (
            <Badge kind="default">{report.correctedExample.generatedBy}</Badge>
          ) : null}
        </SectionTitle>
        {report.correctedExample ? (
          <div className="stack stack--tight">
            <CodeBlock
              title={report.correctedExample.title}
              code={report.correctedExample.content}
              actions={
                <CopyButton
                  text={report.correctedExample.content}
                  id="corrected"
                  label="Copy example"
                />
              }
            />
            {report.correctedExample.notes.length > 0 ? (
              <ul className="plain-list">
                {report.correctedExample.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="faint">
            No corrected example could be produced from this input. Adding the original request
            usually makes one possible.
          </p>
        )}
      </section>

      <section className="card" aria-labelledby="report-checklist">
        <SectionTitle>
          <span id="report-checklist">Verification checklist</span>
        </SectionTitle>
        <ul className="checklist">
          {report.verificationChecklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {errorFindings.length > 0 ? (
        <section className="card" aria-labelledby="report-findings">
          <SectionTitle>
            <span id="report-findings">Deterministic findings</span>
            <span className="small faint">
              {report.deterministicFindings.length}{' '}
              {pluralise(report.deterministicFindings.length, 'check')} matched
            </span>
          </SectionTitle>
          <div className="stack stack--tight">
            {errorFindings.map((finding) => (
              <div key={finding.code} className={`cause cause--${finding.kind}`}>
                <div className="cause__head">
                  <p className="cause__title">{finding.title}</p>
                  <span className="row" style={{ gap: 6 }}>
                    <KindBadge kind={finding.kind} />
                    <Badge kind={finding.severity === 'error' ? 'error' : 'default'}>
                      {finding.severity}
                    </Badge>
                  </span>
                </div>
                <p className="cause__explanation">{finding.detail}</p>
                {finding.evidence.length > 0 ? (
                  <ul className="evidence-list">
                    {finding.evidence.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="small faint mono">{finding.code}</div>
              </div>
            ))}
          </div>

          {otherFindings.length > 0 ? (
            <details style={{ marginTop: 16 }}>
              <summary>Informational checks ({otherFindings.length})</summary>
              <ul className="plain-list">
                {otherFindings.map((finding) => (
                  <li key={finding.code}>
                    <strong>{finding.title}</strong> — {finding.detail}{' '}
                    <span className="faint mono">({finding.code})</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      ) : null}

      <section className="card" aria-labelledby="report-support">
        <SectionTitle>
          <span id="report-support">Support-ready report</span>
          <CopyButton text={report.supportTicketSummary} id="support" label="Copy summary" />
        </SectionTitle>
        <CodeBlock code={report.supportTicketSummary} maxHeight={360} />
      </section>

      <div className="grid grid--2">
        <section className="card" aria-labelledby="report-needed">
          <SectionTitle>
            <span id="report-needed">Information still needed</span>
          </SectionTitle>
          {report.informationStillNeeded.length === 0 ? (
            <p className="faint">Nothing further was identified.</p>
          ) : (
            <ul className="plain-list">
              {report.informationStillNeeded.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>

        <section className="card" aria-labelledby="report-uncertainty">
          <SectionTitle>
            <span id="report-uncertainty">Uncertainty and safety</span>
          </SectionTitle>
          <ul className="plain-list">
            {report.uncertaintyNotes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <ul className="plain-list">
            {report.safetyNotes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>

      <section className="card" aria-labelledby="report-sanitization">
        <SectionTitle>
          <span id="report-sanitization">Sanitization summary</span>
          {report.sanitization.redacted ? (
            <Badge>{report.sanitization.totalRedactions} redactions</Badge>
          ) : (
            <Badge kind="default">no secrets detected</Badge>
          )}
        </SectionTitle>

        <Alert tone="warning" title="Review before sharing">
          {report.sanitization.warning}
        </Alert>

        {report.sanitization.hits.length > 0 ? (
          <ul className="plain-list" style={{ marginTop: 12 }}>
            {report.sanitization.hits.map((hit) => (
              <li key={`${hit.placeholder}-${hit.category}`}>
                <code>{hit.placeholder}</code> × {hit.count} ({hit.category})
              </li>
            ))}
          </ul>
        ) : null}

        {report.sanitization.customSensitiveFields.length > 0 ? (
          <p className="small faint" style={{ marginTop: 8 }}>
            Custom sensitive fields: {report.sanitization.customSensitiveFields.join(', ')}
          </p>
        ) : null}

        {report.sanitization.notes.length > 0 ? (
          <details style={{ marginTop: 12 }}>
            <summary>How sanitization was applied</summary>
            <ul className="plain-list">
              {report.sanitization.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </details>
        ) : null}

        {showSanitizedInput ? (
          <div style={{ marginTop: 12 }}>
            <CodeBlock
              title="Sanitized input that was stored and analysed"
              code={sanitizedInput}
              maxHeight={320}
              actions={<CopyButton text={sanitizedInput} id="sanitized" label="Copy input" />}
            />
          </div>
        ) : null}
      </section>

      <section className="card" aria-labelledby="report-limitations">
        <SectionTitle>
          <span id="report-limitations">Limitations</span>
        </SectionTitle>
        <ul className="plain-list">
          {report.limitations.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
